import BatteryStorageIcon from "../../media/BatteryStorageIcon";
import BiomassIcon from "../../media/BiomassIcon";
import CoalIcon from "../../media/CoalIcon";
import GeothermalIcon from "../../media/GeothermalIcon";
import HydroelectricIcon from "../../media/HydroelectricIcon";
import NaturalGasIcon from "../../media/NaturalGasIcon";
import NuclearIcon from "../../media/NuclearIcon";
import PetroleumIcon from "../../media/PetroleumIcon";
import PumpedStorageIcon from "../../media/PumpedStorageIcon";
import SolarIcon from "../../media/SolarIcon";
import WindIcon from "../../media/WindIcon";
import OtherIcon from "../../media/OtherIcon";
import { TIVisibleFeatures } from "../../types/types";
import { getPaintProperty } from "../../utils/transmission-insights/filter-expression-utils";

export const TI_LAYER_IDS = {
    BUS_LOCATION_LAYER_ID: "bus-location-layer",
    TRANSMISSION_LINES_LAYER_ID: "transmission-lines-layer",
    LOAD_LAYER_ID: "load-layer",
    THERMAL_CAPACITY_NO_CLUSTER_LAYER_ID: "thermal-capacity-no-cluster-layer",
    THERMAL_CAPACITY_WORST_CLUSTER_LAYER_ID:
        "thermal-capacity-worst-cluster-layer",
    GENERATOR_OPERATING_LAYER_ID: "generator-operating-layer",
    GENERATOR_PLANNED_LAYER_ID: "generator-planned-layer",
    GENERATOR_RETIRED_LAYER_ID: "generator-retired-layer",
};

export const LAYER_ID_TYPE_MAP = new Map([
    [TI_LAYER_IDS.BUS_LOCATION_LAYER_ID, TIVisibleFeatures.bus],
    [TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID, TIVisibleFeatures.transmission],
    [TI_LAYER_IDS.LOAD_LAYER_ID, TIVisibleFeatures.load],
    [
        TI_LAYER_IDS.THERMAL_CAPACITY_NO_CLUSTER_LAYER_ID,
        TIVisibleFeatures.thermal,
    ],
    [
        TI_LAYER_IDS.THERMAL_CAPACITY_WORST_CLUSTER_LAYER_ID,
        TIVisibleFeatures.thermal,
    ],
    [TI_LAYER_IDS.GENERATOR_OPERATING_LAYER_ID, TIVisibleFeatures.generator],
    [TI_LAYER_IDS.GENERATOR_PLANNED_LAYER_ID, TIVisibleFeatures.generator],
    [TI_LAYER_IDS.GENERATOR_RETIRED_LAYER_ID, TIVisibleFeatures.generator],
]);

export const TECH_FILTER_PROPS = [
    {
        id: "battery-storage",
        label: "Battery Storage",
        icon: BatteryStorageIcon,
    },
    {
        id: "biomass",
        label: "Biomass",
        icon: BiomassIcon,
    },
    {
        id: "coal",
        label: "Coal",
        icon: CoalIcon,
    },
    {
        id: "geothermal",
        label: "Geothermal",
        icon: GeothermalIcon,
    },
    {
        id: "hydroelectric",
        label: "Hydroelectric",
        icon: HydroelectricIcon,
    },
    {
        id: "natural-gas",
        label: "Natural Gas",
        icon: NaturalGasIcon,
    },
    {
        id: "nuclear",
        label: "Nuclear",
        icon: NuclearIcon,
    },
    {
        id: "petroleum",
        label: "Petroleum",
        icon: PetroleumIcon,
    },
    {
        id: "pumped-storage",
        label: "Hydro - Pumped Storage",
        icon: PumpedStorageIcon,
    },
    {
        id: "solar",
        label: "Solar",
        icon: SolarIcon,
    },
    {
        id: "wind",
        label: "Wind",
        icon: WindIcon,
    },
    {
        id: "other",
        label: "Other",
        icon: OtherIcon,
    },
];

export const TRANSMISSION_LINE_COLORS = {
    "<100kv": "#019600",
    "100kv-200kv": "#00C8CB",
    "200kv-300kv": "#FBA515",
    "300kv-500kv": "#FE0C01",
    "500kv": "#470CA0",
    "765kv": "#E674F0",
    dc: "#000000",
    others: "#AFAFAF",
};

export const LINE_BUS_PROPS = [
    {
        id: "<100kv",
        label: "< 100kV",
        color: TRANSMISSION_LINE_COLORS["<100kv"],
    },
    {
        id: "100kv-200kv",
        label: "100 - 200kV",
        color: TRANSMISSION_LINE_COLORS["100kv-200kv"],
    },
    {
        id: "200kv-300kv",
        label: "200 - 300kV",
        color: TRANSMISSION_LINE_COLORS["200kv-300kv"],
    },
    {
        id: "300kv-500kv",
        label: "300 - 500kV",
        color: TRANSMISSION_LINE_COLORS["300kv-500kv"],
    },
    {
        id: "500kv",
        label: "500kV",
        color: TRANSMISSION_LINE_COLORS["500kv"],
    },
    {
        id: "765kv",
        label: "765kV",
        color: TRANSMISSION_LINE_COLORS["765kv"],
    },
    {
        id: "dc",
        label: "DC",
        color: TRANSMISSION_LINE_COLORS["dc"],
    },
    {
        id: "others",
        label: "Others",
        color: TRANSMISSION_LINE_COLORS["others"],
    },
];

export const LOAD_PROPS = [
    {
        id: "<10mv",
        label: "< 10 MV",
    },
    {
        id: "10mv-50mv",
        label: "10 - 50 MV",
    },
    {
        id: "50mv-100mv",
        label: "50 - 100 MV",
    },
    {
        id: "100mv-200mv",
        label: "100 - 200 MV",
    },
    {
        id: "200mv-300mv",
        label: "200 - 300 MV",
    },
    {
        id: ">300mv",
        label: "> 300 MV",
    },
];

const GENERATOR_CIRCLE_OPTS = {
    "circle-radius": ["interpolate", ["linear"], ["zoom"], 3, 3, 8, 6, 15, 12],
    "circle-opacity": 0.8,
    "circle-stroke-width": 1,
    "circle-stroke-color": "#ffffff",
};

export const TI_BUS_LAYER_SETTINGS = {
    type: "symbol",
    source: "bus-location-source",
    layout: {
        "icon-image": "custom-triangle",
        "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            0.5,
            5,
            0.55,
            10,
            0.7,
            22,
            1,
        ],
        "icon-allow-overlap": false,
        "icon-ignore-placement": true,
        "icon-rotation-alignment": "map",
        "icon-pitch-alignment": "map",
    },
    paint: {
        "icon-color": getPaintProperty("bus")!,
        "icon-opacity": 1,
    },
};

export const TI_LINE_LAYER_SETTINGS = {
    type: "line",
    source: "transmission-lines-source",
    maxzoom: 22,
    layout: {
        "line-join": "round",
        "line-cap": "round",
        visibility: "visible",
        "line-sort-key": ["get", "VOLTAGE"],
    },
    paint: {
        "line-color": getPaintProperty("transmission-lines")!,
        "line-width": 3,
    },
};

export const TI_THERMAL_NO_LAYER_SETTINGS = {
    type: "circle",
    source: "thermal-capacity-source",
    paint: {
        "circle-color": {
            property: "NoClusterTrLim",
            type: "exponential",
            stops: [
                // ATC values (in MW)
                [-40, "#062A78"], // < -40
                [-20, "#4280B9"], // -20
                [0, "#5FA1C7"], // 0
                [10, "#8FC6D7"], // 10
                [20, "#B1F3F8"], // 20
                [30, "#FFFFBF"], // 30
                [50, "#FFE066"], // 50
                [75, "#FFBC3C"], // 75
                [125, "#E87200"], // 125
                [175, "#D21C00"], // 175
                [225, "#D00131"], // > 225
            ],
        },
        "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            3,
            3,
            8,
            6,
            15,
            12,
        ],
    },
};

export const TI_THERMAL_WORST_LAYER_SETTINGS = {
    type: "circle",
    source: "thermal-capacity-source",
    paint: {
        "circle-color": {
            property: "WorstClusterTrLim",
            type: "exponential",
            stops: [
                // ATC values (in MW)
                [-40, "#062A78"], // < -40
                [-20, "#4280B9"], // -20
                [0, "#5FA1C7"], // 0
                [10, "#8FC6D7"], // 10
                [20, "#B1F3F8"], // 20
                [30, "#FFFFBF"], // 30
                [50, "#FFE066"], // 50
                [75, "#FFBC3C"], // 75
                [125, "#E87200"], // 125
                [175, "#D21C00"], // 175
                [225, "#D00131"], // > 225
            ],
        },
        "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            3,
            3,
            8,
            6,
            15,
            12,
        ],
    },
};

export const TI_OPERATING_LAYER_SETTINGS = {
    type: "circle",
    source: "generator-operating-source",
    paint: {
        "circle-color": "#1ABA0E",
        ...GENERATOR_CIRCLE_OPTS,
    },
};

export const TI_PLANNED_LAYER_SETTINGS = {
    type: "circle",
    source: "generator-planned-source",
    paint: {
        "circle-color": "#1596EA",
        ...GENERATOR_CIRCLE_OPTS,
    },
};

export const TI_RETIRED_LAYER_SETTINGS = {
    type: "circle",
    source: "generator-retired-source",
    paint: {
        "circle-color": "#FE0C01",
        ...GENERATOR_CIRCLE_OPTS,
    },
};

export const TI_LOAD_LAYER_SETTINGS = {
    type: "circle",
    source: "load-source",
    paint: {
        "circle-radius": [
            "interpolate",
            ["exponential", 1.5],
            ["zoom"],
            3,
            [
                "interpolate",
                ["linear"],
                ["get", "PL"],
                0,
                3,
                10,
                4,
                50,
                5,
                100,
                6,
                200,
                7,
                300,
                8,
            ],
            8,
            [
                "interpolate",
                ["linear"],
                ["get", "PL"],
                0,
                5,
                10,
                6,
                50,
                8,
                100,
                10,
                200,
                12,
                300,
                14,
            ],
        ],
        "circle-color": "rgba(255, 255, 255, 0.6)",
        "circle-stroke-color": "#4A90E2",
        "circle-stroke-width": 1.5,
        "circle-opacity": 0.9,
    },
};

